import React from 'react'
import { merge, omit } from 'lodash'
import { DateTime } from 'luxon'
import { z } from 'zod'
import { Button, Typography } from '@mui/material'
import { PatientHistoryType } from 'src/graphql.generated'
import { checkIfKaiserHealthPlan } from 'src/participant/participantUtils'
import { ADVANCED_FOLLOW_UP_NOTE_FORM_SCHEMA } from 'src/participantHistoryForm/form/advancedFollowUpNote/AdvancedFollowUpNoteFormSchema'
import { getDefaultValues as getIntakeNoteComprehensiveDefaults } from 'src/participantHistoryForm/form/intakeNoteComprehensive/initialValues/defaultValues'
import { INTAKE_NOTE_COMPREHENSIVE_FORM_SCHEMA } from 'src/participantHistoryForm/form/intakeNoteComprehensive/IntakeNoteComprehensiveFormSchema'
import { getMonthlySummaryNoteFormSchema } from 'src/participantHistoryForm/form/monthlySummaryNote/MonthlySummaryNoteFormSchema'
import { TREATMENT_PLAN_COMPREHENSIVE_FORM_SCHEMA } from 'src/participantHistoryForm/form/treatmentPlanComprehensive/TreatmentPlanComprehensiveFormSchema'
import { useGetAdvancedParticipantQuery } from '../../../advancedParticipant.generated'
import { useIntakeNoteComprehensivePrefill, usePatientHistoryPrefill } from '../../../api/usePatientHistoryPrefill'
import { advancedColors } from '../../../theme'
import {
  ADVANCED_FOLLOW_UP_NOTE_SECTIONS,
  INTAKE_NOTE_COACHING_SECTIONS,
  INTAKE_NOTE_COMPREHENSIVE_SECTIONS,
  MONTHLY_SUMMARY_NOTE_SECTIONS,
  TREATMENT_PLAN_COMPREHENSIVE_SECTIONS,
} from '../comprehensiveLayouts'
import PrefillLoading from './PrefillLoading'
import SpecForm from './SpecForm'

type Values = Record<string, unknown>

const MONTHLY_SUMMARY_NOTE_DEFAULTS: Values = {
  treatmentPlan: { smartGoals: '' },
  participantEngagement: { participation: '' },
}

const requireSmartGoals = (schema: z.ZodTypeAny) =>
  z.any().superRefine((data, ctx) => {
    const parsed = schema.safeParse(data)

    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => ctx.addIssue(issue))
    }

    const goals = (data as { treatmentPlan?: { smartGoals?: string } }).treatmentPlan?.smartGoals

    if (!goals?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['treatmentPlan', 'smartGoals'] })
    }
  })

const TREATMENT_PLAN_COMPREHENSIVE_DEFAULTS: Values = {
  PRESENTING_PROBLEM: '',
  LONG_TERM_GOAL: '',
  SHORT_TERM_GOAL: {
    SPECIFIC: '',
    MEASURABLE: '',
    ACHIEVABLE: 'Yes',
    RELEVANT: '',
    TIME_BOUND: '12 weeks',
    GOAL_STATEMENT: '',
  },
  INTERVENTIONS_AND_PARTICIPANT_AGREEMENT: {
    PHQ9_GAD7: true,
    BIOFEEDBACK: true,
    IN_APP_CHAT: true,
    CBT_MINDFULLNESS_ACT: true,
    PSYCHOEDUCATION: true,
    TELEHEALTH_VIDEO: true,
    PEER_SUPPORT: true,
    PARTICIPANT_AGREEMENT: false,
  },
}

const PrefilledSpecForm = ({
  uid,
  type,
  defaults = {},
  without = [],
  sections,
  schema,
  showDate,
}: {
  uid: string
  type: PatientHistoryType
  defaults?: Values
  without?: string[]
  showDate?: boolean
  sections: React.ComponentProps<typeof SpecForm>['sections']
  schema: z.ZodTypeAny
}) => {
  const { loading, value } = usePatientHistoryPrefill(uid, type)

  return loading ? (
    <PrefillLoading />
  ) : (
    <SpecForm
      uid={uid}
      type={type}
      sections={sections}
      schema={schema}
      initial={merge({}, defaults, omit(value, without))}
      showDate={showDate}
    />
  )
}

const INTAKE_NOTE_CONFIRM = { buttonLabel: 'Intake note is complete', title: 'Complete Intake Note' }

const INTAKE_NOTE_COACHING_SCHEMA = z.object({ notes: z.string().trim().min(1, 'Intake notes are required') })

export const IntakeNoteCoachingFields = ({ uid }: { uid: string }) => (
  <SpecForm
    uid={uid}
    type={PatientHistoryType.IntakeNoteCoaching}
    sections={INTAKE_NOTE_COACHING_SECTIONS}
    schema={INTAKE_NOTE_COACHING_SCHEMA}
    dateInValue={false}
    confirm={INTAKE_NOTE_CONFIRM}
  />
)

export const AdvancedFollowUpNoteFields = ({ uid }: { uid: string }) => (
  <SpecForm
    uid={uid}
    type={PatientHistoryType.AdvancedFollowUpNote}
    sections={ADVANCED_FOLLOW_UP_NOTE_SECTIONS}
    schema={ADVANCED_FOLLOW_UP_NOTE_FORM_SCHEMA}
    showDate={false}
    confirm={{ buttonLabel: 'Follow Up Note is complete', title: 'Complete Follow Up Note' }}
  />
)

export const TreatmentPlanComprehensiveFields = ({ uid }: { uid: string }) => (
  <PrefilledSpecForm
    uid={uid}
    type={PatientHistoryType.TreatmentPlanComprehensive}
    defaults={TREATMENT_PLAN_COMPREHENSIVE_DEFAULTS}
    sections={TREATMENT_PLAN_COMPREHENSIVE_SECTIONS}
    schema={TREATMENT_PLAN_COMPREHENSIVE_FORM_SCHEMA}
  />
)

export const TreatmentPlanComprehensiveUpdateFields = ({ uid }: { uid: string }) => (
  <PrefilledSpecForm
    uid={uid}
    type={PatientHistoryType.TreatmentPlanComprehensiveUpdate}
    defaults={TREATMENT_PLAN_COMPREHENSIVE_DEFAULTS}
    sections={TREATMENT_PLAN_COMPREHENSIVE_SECTIONS}
    schema={TREATMENT_PLAN_COMPREHENSIVE_FORM_SCHEMA}
  />
)

// The health plan decides both the CPT field and the schema, so the form waits for it as Standard's does.
export const MonthlySummaryNoteFields = ({ uid }: { uid: string }) => {
  const { data, loading, error, refetch } = useGetAdvancedParticipantQuery({ variables: { uid } })
  const isKaiser = Boolean(checkIfKaiserHealthPlan(data?.patient?.credentialingHealthPlan))

  if (loading) {
    return <PrefillLoading />
  }

  if (error) {
    return (
      <Typography variant="body2" sx={{ color: advancedColors.error }}>
        Failed to load the participant’s health plan.{' '}
        <Button size="small" onClick={() => refetch()}>
          Retry
        </Button>
      </Typography>
    )
  }

  return (
    <PrefilledSpecForm
      uid={uid}
      type={PatientHistoryType.MonthlySummaryNote}
      defaults={{ ...MONTHLY_SUMMARY_NOTE_DEFAULTS, ...(isKaiser && { cptCode: '' }) }}
      without={isKaiser ? [] : ['cptCode']}
      sections={MONTHLY_SUMMARY_NOTE_SECTIONS}
      schema={requireSmartGoals(getMonthlySummaryNoteFormSchema(isKaiser))}
      showDate={false}
    />
  )
}

// Standard dates the intake on the day of the call it was prefilled from.
const callDay = (value: Values) => {
  const start = value.CALL_START_TIME

  return typeof start === 'string' && start ? DateTime.fromISO(start).toFormat('yyyy-MM-dd') : undefined
}

export const IntakeNoteComprehensiveFields = ({ uid }: { uid: string }) => {
  const { loading, value } = useIntakeNoteComprehensivePrefill(uid)

  return loading ? (
    <PrefillLoading />
  ) : (
    <SpecForm
      uid={uid}
      type={PatientHistoryType.IntakeNoteComprehensive}
      sections={INTAKE_NOTE_COMPREHENSIVE_SECTIONS}
      schema={INTAKE_NOTE_COMPREHENSIVE_FORM_SCHEMA}
      initial={merge({}, getIntakeNoteComprehensiveDefaults(), value)}
      initialDate={callDay(value)}
      confirm={INTAKE_NOTE_CONFIRM}
    />
  )
}
